import type { UsersListFilters } from "./types";
import { DEFAULT_PAGE_SIZE } from "./constants";

/**
 * Build the query string for `GET /api/admin/users` from the filters
 * the page state carries. Empty / default values are omitted so the
 * URL stays minimal, the React Query cache key matches between the UI
 * and the network call, and switching pages doesn't constantly toggle
 * a `limit=25` segment that the backend treats as the default anyway.
 *
 * Returns an empty string (no leading `?`) when no params are set, so
 * callers can do `${path}${buildUsersQuery(filters)}` safely.
 */
export function buildUsersQuery(filters: UsersListFilters): string {
  const params = new URLSearchParams();

  const trimmed = filters.query.trim();
  if (trimmed.length > 0) params.set("query", trimmed);
  // Backend expects `roles` (array, comma-separated per its Swagger),
  // NOT `role` (singular). The UI still tracks a single active role
  // filter — comma-separated multi-select is a follow-up.
  if (filters.role) params.set("roles", filters.role);
  if (filters.status !== "all") params.set("status", filters.status);
  if (filters.page > 1) params.set("page", String(filters.page));
  if (filters.limit > 0 && filters.limit !== DEFAULT_PAGE_SIZE) {
    params.set("limit", String(filters.limit));
  }

  const qs = params.toString();
  return qs.length > 0 ? `?${qs}` : "";
}
